import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslation from './locales/en/translation.json';

// Ordered by internet/website content usage (source: Statista, W3Techs 2025)
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', dir: 'ltr' },           // ~49% of web
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dir: 'ltr' },          // ~6% of web
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },           // ~5.6% of web
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', dir: 'ltr' },          // ~4.5% of web
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', dir: 'ltr' },          // ~4.3% of web
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },          // ~4.3% of web
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dir: 'ltr' },             // ~3.5% of web
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', dir: 'ltr' },    // ~2.5% of web
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', dir: 'ltr' },         // ~2% of web
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', dir: 'ltr' },            // ~1.8% of web
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', dir: 'ltr' },         // ~1.5% of web
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },           // ~1.4% of web
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', dir: 'ltr' },            // ~1.2% of web
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },          // ~1% of web
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },   // ~0.9% of web
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩', dir: 'ltr' }, // ~0.7% of web
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', dir: 'ltr' },                 // ~0.6% of web
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦', dir: 'ltr' },    // ~0.5% of web
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },            // ~0.1% of web
];

// RTL languages
export const rtlLanguages = ['ar', 'he', 'fa', 'ur'];

// Check if a language is RTL
export const isRTL = (langCode) => rtlLanguages.includes(langCode);

// Start with only English, other languages will be added dynamically
const resources = {
  en: { translation: enTranslation },
};

// Language detection options for URL-based detection
const detectionOptions = {
  order: ['path', 'localStorage', 'navigator'],
  lookupFromPathIndex: 0,
  caches: ['localStorage'],
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: detectionOptions,
  });

// Function to dynamically load and add a language
export const loadLanguage = async (langCode) => {
  if (i18n.hasResourceBundle(langCode, 'translation')) {
    return; // Already loaded
  }

  try {
    const translation = await import(`./locales/${langCode}/translation.json`);
    i18n.addResourceBundle(langCode, 'translation', translation.default, true, true);
  } catch (error) {
    console.warn(`Failed to load language: ${langCode}`, error);
  }
};

export default i18n;
